
import React, { useMemo, useCallback, useState, useContext } from 'react'
import { date } from 'utils'

import { usePackages } from 'hooks/api'
import { useWorkOSAuth } from 'hooks'
import { DevToolsContext } from 'contexts'
import { BoxIcon, LinkIcon, PackagePlusIcon, TagIcon, TrashIcon, UploadIcon, WarehouseIcon } from 'lucide-react'
import { Stack } from '@mui/material'

import { Button } from '@repo/ui'
import { useCanAccess } from 'hooks'
import { ROUTES } from 'routes'
import { PACKAGE_FILL_STATUSES, CONSIGNMENT_DESTINATIONS } from 'constants/statuses'
import { isInternalRoleName } from 'constants/roles'
import {
  EmptyState,
  Header,
  Table,
  ActionDropdown,
  PackageBoxModal,
  DeletePackageModal,
  EncodePackageHandleModal,
  EncodeTagsModal,
  BulkEncodeTagsModal,
  ImportPackageModal,
  SetPackageCompanyModal,
  AssignPackageProductModal,
  SetConsignmentDestinationModal,
  ProductCell,
  PackageCell,
  CompanyCell,
  LinkCell,
  PermissionGate,
  StatusChip,
  familyForTagShopProduct,
} from 'components'
import type { EncodeSessionTarget } from 'components'


// VERA-790: a box's fill progress — actual tags (`quantity`) vs. its expected capacity (the
// selected variant's quantity, e.g. "Qty 100"). Drives both the "N / M" Quantity cell and the
// Empty / Partial / Filled status chip. With no variant assigned there's no target, so it degrades
// to Empty (0) / Filled (>0).
const packageFill = (row: any) => {
  const filled = Number(row?.quantity ?? 0)
  const tsp = row?.tag_shop_product
  const expected =
    tsp?.variants?.find((v: any) => v?.id === tsp?.selected_variant?.id)?.quantity ?? null
  const status = filled === 0 ? 'empty' : expected && filled < expected ? 'partial' : 'filled'
  return { filled, expected: expected ?? null, status }
}

export const PackagesPage = () => {
  const { devToolsEnabled } = useContext(DevToolsContext) || true

  const [selectedPackage, setSelectedPackage] = useState(null)
  const [openDeletePackageModal, setOpenDeletePackageModal] = useState(false)
  const [openImportPackageModal, setOpenImportPackageModal] = useState(false)
  const [openSetCompanyModal, setOpenSetCompanyModal] = useState(false)
  // VRG-282 — pre-assign the whole box to one of the owning company's products.
  const [openAssignProductModal, setOpenAssignProductModal] = useState(false)
  // VRG-455 — record where the box physically went. Not metadata: `retail_or_held`
  // is what stops a buyer's scan claiming a certificate for stock on a shelf.
  const [openDestinationModal, setOpenDestinationModal] = useState(false)
  const [openAddPackagesModal, setOpenAddPackagesModal] = useState(false)
  const [openEncodeHandleModal, setOpenEncodeHandleModal] = useState(false)
  // VERA-790 — single-package "Add tags" now runs in a modal (was a full page). Holds the resolved
  // fill target for the currently-open session.
  const [encodeTagsTarget, setEncodeTagsTarget] = useState<EncodeSessionTarget | null>(null)
  const [openEncodeTagsModal, setOpenEncodeTagsModal] = useState(false)
  // VERA-790 — the bulk assembly-line "Add tags" cycle now also runs in a modal (was a full page):
  // 'scan' (top-bar button, scan each box's handle) or 'queue' (table bulk-select, walk the picked
  // rows in order).
  const [bulkTagsMode, setBulkTagsMode] = useState<'scan' | 'queue'>('scan')
  const [bulkTagsQueue, setBulkTagsQueue] = useState<EncodeSessionTarget[] | undefined>(undefined)
  const [openBulkTagsModal, setOpenBulkTagsModal] = useState(false)

  const { user } = useWorkOSAuth()
  // VRG-410 — the shared predicate rather than a local pair. This value feeds only a
  // dependency array today, but a stale copy of "which roles are internal" reads as
  // authoritative to whoever picks it up next, which is how the original defect spread.
  const isInternalUser = isInternalRoleName(user?.role?.name)
  const { isHasAccess: canEncodeHandle } = useCanAccess({ action: 'internal_package_upload' })
  // VRG-253 — direct assignment is a real capability with its own permission, not a dev tool.
  const { isHasAccess: canAssignDirectly } = useCanAccess({ action: 'internal_assign_directly' })
  // VRG-282 — binding a package IS a way of creating certificates, so it carries its own
  // permission rather than riding on `manage_certificate`. Gated in the UI as well as on the
  // endpoint: without this the action would look live and answer 403 on Save.
  const { isHasAccess: canBindPackageProduct } = useCanAccess({
    action: 'certificate_create_from_package',
  })
  // VRG-761 — the delete endpoint had NO permission at all until now, so this row was enabled for
  // everyone `RequireAdmin`'s implicit role list admitted. `internal_package_delete` is
  // platform_admin only; handling stock is not destroying it.
  const { isHasAccess: canDeletePackage } = useCanAccess({ action: 'internal_package_delete' })

  // Server-side pagination + sort + filter state
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 })
  const [sortField, setSortField] = useState<string | undefined>(undefined)
  const [sortDir, setSortDir] = useState<'asc' | 'desc' | undefined>(undefined)
  // VERA-790 server-side filters, derived from the grid filter model (see onFilterModelChange).
  const [statusFilter, setStatusFilter] = useState<'empty' | 'partial' | 'filled' | undefined>(undefined)
  const [tagFilter, setTagFilter] = useState<string | undefined>(undefined)

  const { data, isLoading, refetch } = usePackages({
    limit: paginationModel.pageSize,
    offset: paginationModel.page * paginationModel.pageSize,
    sortField,
    sortDir,
    status: statusFilter,
    tag: tagFilter,
  })

  const packages = data?.data ?? []

  const handleCloseImportPackageModal = () => {
    setSelectedPackage(null)
    setOpenImportPackageModal(false)
  }

  // Link order functionality not available yet

  const handleDeletePackage = (row) => {
    setSelectedPackage(row)
    setOpenDeletePackageModal(true)
  }

  const handleCloseDeletePackageModal = () => {
    setSelectedPackage(null)
    setOpenDeletePackageModal(false)
  }

  const handleSetCompany = (row) => {
    setSelectedPackage(row)
    setOpenSetCompanyModal(true)
  }

  const handleCloseSetCompanyModal = () => {
    setSelectedPackage(null)
    setOpenSetCompanyModal(false)
  }

  const handleAssignProduct = (row: any) => {
    setSelectedPackage(row)
    setOpenAssignProductModal(true)
  }

  const handleCloseAssignProductModal = () => {
    setSelectedPackage(null)
    setOpenAssignProductModal(false)
  }

  const handleSetDestination = (row: any) => {
    setSelectedPackage(row)
    setOpenDestinationModal(true)
  }

  const handleCloseDestinationModal = () => {
    setSelectedPackage(null)
    setOpenDestinationModal(false)
  }

  const handleEncodeHandle = (row) => {
    setSelectedPackage(row)
    setOpenEncodeHandleModal(true)
  }

  const handleCloseEncodeHandleModal = () => {
    setSelectedPackage(null)
    setOpenEncodeHandleModal(false)
  }

  // VERA-790 "Add Packages" redesign — FILL-THE-CONTAINER phase. Builds the EncodeSessionTarget the
  // encode modals consume, exactly like EncodeConfigureModal's scan-resolve path: derive the
  // family/product identity from the package's own tag_shop_product association (set when the
  // box was registered). Shared by the single-row kebab action AND the bulk-select action below
  // (which builds an ordered queue of these targets).
  const packageRowToTarget = useCallback((row): EncodeSessionTarget => {
    const tagShopProduct = row?.tag_shop_product ?? null
    return {
      kind: 'package',
      id: row.id,
      name: row.name,
      family: familyForTagShopProduct(tagShopProduct),
      productSku: tagShopProduct?.selected_variant?.sku ?? tagShopProduct?.sku ?? undefined,
      productTitle: tagShopProduct?.selected_variant?.title ?? tagShopProduct?.title ?? undefined,
      productImage:
        tagShopProduct?.selected_variant?.image ?? tagShopProduct?.images?.[0] ?? undefined,
      lock: false,
      // VERA-790 Part B — mirrors packageFill's `expected` so the encode session's counter shows
      // the same "N / target" the Packages table already shows for this row.
      targetCount: packageFill(row).expected ?? undefined,
    }
  }, [])

  // Single-package entry — the row kebab's "Add tags". Opens that package's encode session in a
  // modal (was a full-page navigation), matching the Create Certificates reader-modal idiom. The
  // bulk scan/queue flows below still use the full-page assembly line.
  const handleEncodeTags = (row) => {
    setEncodeTagsTarget(packageRowToTarget(row))
    setOpenEncodeTagsModal(true)
  }

  const handleCloseEncodeTagsModal = () => {
    setOpenEncodeTagsModal(false)
    setEncodeTagsTarget(null)
  }

  // VERA-790 (coordinator follow-up) — bulk-select "Add tags": the operator checks N rows in the
  // table and this walks the SAME bulk assembly-line cycle over exactly those packages, in
  // selection order, instead of resolving each box by scanning its handle. Opens the bulk modal
  // in 'queue' mode with a pre-built ordered target queue.
  const handleBulkAddTags = useCallback(
    (selectedRows: any[]) => {
      setBulkTagsQueue(selectedRows.map(packageRowToTarget))
      setBulkTagsMode('queue')
      setOpenBulkTagsModal(true)
    },
    [packageRowToTarget],
  )

  const handleCloseBulkTagsModal = () => {
    setOpenBulkTagsModal(false)
    setBulkTagsQueue(undefined)
  }

  const getActionItems = useCallback((row) => {
    const items = []

    items.push({
      label: 'Add tags',
      onClick: () => handleEncodeTags(row),
      divider: false,
      destructive: false,
      visible: true,
      disabled: !canEncodeHandle,
      tooltip: !canEncodeHandle
        ? "You need the 'Upload packages' permission. Ask an admin to grant it."
        : undefined,
      icon: <TagIcon strokeWidth={1} />,
    })

    // VRG-282 — bind the box to one of the owning company's products, so the certificates exist
    // before it ships and the contract manufacturer only has to place the tag. Available for a
    // box that shipped unbound too (rebinding is allowed until a certificate in it is activated;
    // the API answers 409 after that and the modal surfaces the reason). Needs a company: the
    // product catalog is that company's.
    items.push({
      label: row?.product ? 'Change product' : 'Assign product',
      onClick: () => handleAssignProduct(row),
      divider: false,
      destructive: false,
      visible: true,
      // Two independent reasons to refuse, so the tooltip names the one that actually applies
      // rather than collapsing both into a single "unavailable".
      disabled: !row?.company_id || !canBindPackageProduct,
      tooltip: !canBindPackageProduct
        ? "You need the 'Create from package' permission. Ask an admin to grant it."
        : !row?.company_id
          ? 'Assign this package to a company first. Products belong to a company.'
          : undefined,
      icon: <LinkIcon strokeWidth={1} />,
    })

    // VRG-455 — record where the box went. Sits next to "Assign product" because
    // it is the same decision about the same box by the same person, and carries
    // the same permission for that reason. Unlike the product binding it does NOT
    // need a company: a destination is a fact about the shipment, not about a
    // catalog.
    items.push({
      label: 'Set destination',
      onClick: () => handleSetDestination(row),
      divider: false,
      destructive: false,
      visible: true,
      disabled: !canBindPackageProduct,
      tooltip: !canBindPackageProduct
        ? "You need the 'Create from package' permission. Ask an admin to grant it."
        : undefined,
      icon: <WarehouseIcon strokeWidth={1} />,
    })

    // VERA-790: "Add package tag" (retrofit a box's scannable handle label) removed from the kebab —
    // a package is now always registered WITH its label by scanning the box in the Add packages
    // modal, so there's no create-then-label path. `handleEncodeHandle` is kept for future recovery.

    // VRG-253 — was behind the dev-tools flag, so the capability existed but was invisible to
    // the internal admins meant to use it, and its endpoint (PATCH /packages/:id) carried no
    // permission at all. Now gated on the real permission, granted to platform_admin only.
    // §27 — stays visible+disabled+tooltip without the permission (was hidden entirely behind
    // `if (canAssignDirectly)`), matching "Add tags" just above.
    items.push({
      // Not "Assign Package to Company" — the kebab belongs to a package row, so "Package"
      // restates what the row already is. Same label as the tags-side assign step.
      label: 'Assign to Company',
      onClick: () => handleSetCompany(row),
      divider: false,
      destructive: false,
      visible: true,
      disabled: !canAssignDirectly,
      tooltip: !canAssignDirectly
        ? "You need the 'Assign directly' permission. Ask a Vera admin."
        : undefined,
      icon: <PackagePlusIcon strokeWidth={1} />
    })
    // VRG-761 — §27/§46: a PERMISSION block stays visible, disabled, with a tooltip naming the
    // permission, because resolving it is somebody else's job and the disabled row is the only
    // channel that tells the operator the capability exists. The glyph stays a Trash rather than
    // an X (§38): the package row genuinely ceases to exist. The TAGS inside do not, which is
    // what the confirmation modal is now for.
    items.push({
        label: 'Delete',
        onClick: () => handleDeletePackage(row),
        divider: true,
        destructive: true,
        visible: true,
        disabled: !canDeletePackage,
        tooltip: !canDeletePackage
          ? "You need the 'Delete packages' permission. Ask a Vera admin."
          : undefined,
        icon: <TrashIcon strokeWidth={1} />
    })

    return items
  // Both flags: VERA-455's direct-assign gate and VRG-282's bind-product permission. The
  // callback reads each, so dropping either on a merge would freeze that action's enabled
  // state at first render.
  }, [devToolsEnabled, isInternalUser, canEncodeHandle, canAssignDirectly, canBindPackageProduct, canDeletePackage]
  )

  // Bulk-select action bar (Table's row-selection checkboxes) — gated on the same permission as
  // the row kebab's "Add tags" (§24). §27 — disabled+tooltip, not hidden, matching the kebab item.
  const getBulkActions = useCallback(
    (selectedRows: any[]) => [
      {
        label: 'Add tags',
        onClick: () => handleBulkAddTags(selectedRows),
        icon: <TagIcon strokeWidth={1} />,
        visible: true,
        disabled: !canEncodeHandle,
        tooltip: !canEncodeHandle
          ? "You need the 'Upload packages' permission. Ask an admin to grant it."
          : undefined,
      },
    ],
    [canEncodeHandle, handleBulkAddTags],
  )

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Package',
        type: 'string',
        // House default pinned-column width — lines up the right border with
        // the search box above it.
        width: 368,
        pinned: 'left',
        align: 'left',
        headerAlign: 'left',
        renderCell: ({ row }) => <PackageCell pkg={row} width={280} />,
      },
      {
        field: 'id',
        headerName: 'Package ID',
        width: 340,
        type: 'string',
        valueFormatter: (value) => ("#PKG-" + value)
      },
      {
        field: 'tag_shop_product',
        headerName: 'Tag',
        // VERA-790: 'string' renders the SearchFilter (search box + NFC/QR scan button) like the
        // other filters, instead of the multiSelect product-card list.
        type: 'string',
        width: 240,
        // Support both grid cell rendering (receives row) and filter option rendering (receives value + filter)
        renderCell: ({ row, value, filter }: { row?: any; value?: any; filter?: boolean }) => {
          const product = filter ? value : row?.tag_shop_product
          if (!product) return '-'
          const route = product?.id ? ROUTES.TAGS_SHOP_PRODUCT(product.id) : undefined
          return <ProductCell product={product} route={route} />
        }
      },
      {
        // VRG-282: the BRAND's product this box is pre-assigned to — distinct from `tag_shop_product`
        // above, which is the tag SKU it was purchased as. Without this column the assign action
        // was unverifiable from the list it is invoked from (same reasoning as the Company column).
        field: 'product',
        headerName: 'Product',
        type: 'string',
        width: 240,
        sortable: false,
        // The shared ProductCell carries the image plate, the link, and the referrer state that
        // gives the product page a "← Back to Packages" link (§42).
        renderCell: ({ row }: { row: any }) =>
          row?.product?.id ? (
            <ProductCell
              // The payload projection carries a single `image`; ProductCell reads the catalog's
              // `images[]`. Adapted here rather than special-casing the shared cell.
              product={{
                ...row.product,
                images: row.product.image ? [row.product.image] : [],
              }}
              route={ROUTES.PRODUCT(row.product.id)}
            />
          ) : (
            '-'
          ),
      },
      {
        // VRG-455 — where this box physically went, which decides whether a
        // buyer's scan may claim the certificates for the tags in it. On the
        // list because "not recorded" is the state that leaves shelf stock
        // claimable, and a state nobody can see is one nobody fixes.
        field: 'consignment_destination',
        headerName: 'Destination',
        type: 'string',
        // Sized for the HEADER and for the longest label ("Retail or held
        // stock"), not for the enum values (§49). No sort icon to budget for —
        // the list's sort is a server-side whitelist this column is not in, so
        // it is not sortable rather than sortable-and-broken.
        width: 200,
        sortable: false,
        renderCell: ({ row }: { row: any }) =>
          CONSIGNMENT_DESTINATIONS[
            (row?.consignment_destination ?? 'unknown') as keyof typeof CONSIGNMENT_DESTINATIONS
          ]?.label ?? '-',
      },
      {
        field: 'quantity',
        headerName: 'Quantity',
        type: 'number',
        width: 120,
        // Show progress toward the box's target ("6 / 100") when a variant/capacity is known;
        // bare count when it isn't (an empty box registered without a product yet).
        renderCell: ({ row }) => {
          const { filled, expected } = packageFill(row)
          return expected != null ? `${filled} / ${expected}` : String(filled)
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        // VERA-790: 'statusChip' renders the pill/chip selector in the filter panel (like VERA-246 /
        // 644), NOT a search box. Options are the package fill statuses. valueGetter exposes the
        // computed fill status so the filter operates on it (the row has no raw `status` field).
        type: 'statusChip',
        options: Object.entries(PACKAGE_FILL_STATUSES).map(([value, s]: [string, any]) => ({
          value,
          label: s.label,
          color: s.color,
        })),
        width: 140,
        sortable: false,
        disableColumnMenu: true,
        valueGetter: (_value: any, row: any) => packageFill(row).status,
        renderCell: ({ row }) => (
          <StatusChip
            size="small"
            statuses={PACKAGE_FILL_STATUSES}
            status={packageFill(row).status}
          />
        ),
      },
      {
        field: 'created_at',
        headerName: 'Created',
        type: 'dateTime',
        width: 200,
        renderCell: ({ row }) => date(row.created_at, 'MMM dd yyyy, h:mm a'),
      },
      {
        // VERA-790: stamped when the package first reaches capacity (server-side, during provision).
        field: 'filled_at',
        headerName: 'Filled',
        type: 'dateTime',
        width: 200,
        renderCell: ({ row }: { row: any }) =>
          row.filled_at ? date(row.filled_at, 'MMM dd yyyy, h:mm a') : '-',
      },
      {
        // The owner. Added alongside the "Assign to Company" action (VRG-253) — without it the
        // assign was unverifiable from the list it is invoked from: you could hand a box to a
        // company and the table would look identical afterwards.
        field: 'company',
        headerName: 'Company',
        type: 'string',
        width: 240,
        // The SHARED CompanyCell, not a hand-rolled avatar + name: it carries the logo plate,
        // the link to the company, and the referrer state that gives that page a "← Back to
        // Packages" link with this list's filters intact (§42). The Tags page still hand-rolls
        // its own Company column — converge it when that page is next touched (§36).
        renderCell: ({ row }: { row: any }) =>
          row?.company?.id ? <CompanyCell company={row.company} /> : '-',
      },
      {
        // The order this box was fulfilled against. Links to the INTERNAL order page, not
        // Shopify's. Shows the id parsed from the Shopify GID rather than the human order
        // number (#1001) — the link row holds only the GID and the local `orders` table has no
        // GID to join to, so the name isn't available without caching it at link time.
        field: 'order',
        headerName: 'Order',
        type: 'string',
        width: 140,
        sortable: false,
        renderCell: ({ row }: { row: any }) =>
          row?.order?.id ? (
            <LinkCell
              align="left"
              route={ROUTES.INTERNAL_ORDER(encodeURIComponent(String(row.order.id)))}
              link={`#${row.order.id}`}
            />
          ) : (
            '-'
          ),
      },
      {
        field: 'actions',
        headerName: false,
        pinned: 'right',
        sortable: false,
        disableReorder: true,
        align: 'center',
        width: 54,
        renderCell: ({ row }) => (<ActionDropdown items={getActionItems(row)} />),
      },
    ], []
  )

  return (
    <>
      <Header
        title="Packages"
        rightComponents={
          <Stack direction="row" spacing={4} alignItems="center">
            {/* Upload CSV — HIDDEN per request (VERA-790); NOT deleted, kept for future external
                encoder reuse. Flip `false` → `true` to restore. */}
            {false && (
              <Button
                variant="secondary"
                disableElevation
                onClick={() => setOpenImportPackageModal(true)}
                startIcon={<UploadIcon strokeWidth={1} />}
              >
                Upload CSV
              </Button>
            )}
            {/* VERA-790 combined box flow — ONE modal: scan a box (register it if blank), then fill
                it with tags, then next box. Replaces the old separate "Add tags" (scan) + "Add
                packages" (register) buttons. */}
            <PermissionGate permission="internal_package_upload">
              <Button
                variant="primary"
                disableElevation
                onClick={() => setOpenAddPackagesModal(true)}
                startIcon={<BoxIcon strokeWidth={1} />}
              >
                Add packages
              </Button>
            </PermissionGate>
          </Stack>
        }
      />

      <Table
        fillBottom
        loading={isLoading}
        columns={columns}
        rows={packages}
        // Package ID and Tag are HIDDEN by default (both still toggleable via the Columns menu).
        // The id is a raw uuid nobody reads off a list, and the Tag product is already the image
        // + identity carried by the pinned Package cell, so both were width without information.
        visibilityModel={{ id: false, tag_shop_product: false }}
        bulkActions={getBulkActions}
        paginationMode={'server'}
        filterMode="server"
        onFilterModelChange={(model: any) => {
          // Derive the server filter params from the grid filter model. Status is the statusChip
          // multi-select (first value used server-side); tag is the product search/scan value.
          const items = model?.items ?? []
          const statusItem = items.find((i: any) => i.field === 'status')
          const tagItem = items.find((i: any) => i.field === 'tag_shop_product')
          const s = Array.isArray(statusItem?.value) ? statusItem.value[0] : statusItem?.value
          setStatusFilter(s === 'empty' || s === 'partial' || s === 'filled' ? s : undefined)
          setTagFilter((tagItem?.value as string)?.trim() || undefined)
          setPaginationModel((prev) => ({ ...prev, page: 0 }))
        }}
        rowCount={data?.total ?? 0}
        paginationModel={paginationModel}
        onPaginationModelChange={(model) => setPaginationModel(model)}
        sortingMode="server"
        onSortModelChange={(model) => {
          const first = model[0]
          if (first) {
            setSortField(first.field)
            setSortDir(first.sort as 'asc' | 'desc')
          } else {
            setSortField(undefined)
            setSortDir(undefined)
          }
          setPaginationModel((prev) => ({ ...prev, page: 0 }))
        }}
        emptyState={
          <EmptyState
            title="No packages yet"
            text="Use the Add Packages action above to register one"
            icon={<BoxIcon strokeWidth={1} size={32} />}
          />
        }
      />
      {/* <LinkPackageModal
          packageObj={selectedPackage}
          open={openLinkPackageModal}
          onClose={handleCloseLinkPackageModal}
          refetch={refetch}
        /> */}
      <DeletePackageModal
        packageObj={selectedPackage}
        open={openDeletePackageModal}
        onClose={handleCloseDeletePackageModal}
        refetch={refetch}
      />

      <ImportPackageModal
        open={openImportPackageModal}
        onClose={handleCloseImportPackageModal}
        refetch={refetch}
      />

      <SetPackageCompanyModal
        packageObj={selectedPackage}
        open={openSetCompanyModal}
        onClose={handleCloseSetCompanyModal}
        refetch={refetch}
      />

      <AssignPackageProductModal
        packageObj={selectedPackage}
        open={openAssignProductModal}
        onClose={handleCloseAssignProductModal}
        refetch={refetch}
      />

      {/* VRG-455 — shared with the brand's own order page. */}
      <SetConsignmentDestinationModal
        packageObj={selectedPackage}
        open={openDestinationModal}
        onClose={handleCloseDestinationModal}
        refetch={refetch}
        canSet={canBindPackageProduct}
      />

      <EncodePackageHandleModal
        packageObj={selectedPackage}
        open={openEncodeHandleModal}
        onClose={handleCloseEncodeHandleModal}
        refetch={refetch}
      />

      <EncodeTagsModal
        target={encodeTagsTarget}
        open={openEncodeTagsModal}
        onClose={handleCloseEncodeTagsModal}
        refetch={refetch}
      />

      <BulkEncodeTagsModal
        open={openBulkTagsModal}
        mode={bulkTagsMode}
        queue={bulkTagsQueue}
        onClose={handleCloseBulkTagsModal}
        refetch={refetch}
      />

      <PackageBoxModal
        open={openAddPackagesModal}
        onClose={() => setOpenAddPackagesModal(false)}
        refetch={refetch}
      />
    </>
  )
}
